// src/pages/GWwinner.jsx
import { useEffect, useMemo, useState } from "react";
import {
  fetchFPLData,
  fetchGwTop,
  fetchClassicLeague,
  fetchTieAnalyzer,
} from "../api/fplApi";
import GwTopScoreBench from "../components/GwTopScoreBench";
import GwTopScoreTransfer from "../components/GwTopScoreTransfer";

// const LEAGUE_ID = YOUR_LEAGUE_ID;

// Small badge to show when a tie was present
const TieBadge = ({ title = "Tie resolved" }) => (
  <span
    title={title}
    className="inline-flex items-center gap-1 text-[11px] px-2 py-[2px] rounded-full bg-purple-100 text-purple-700 ring-1 ring-purple-200 ml-2"
  >
    ⚖️ <span className="hidden sm:inline">TIE</span>
  </span>
);

export default function GWwinner({ leagueId = 136323, winnerCount = 3, title = "S16", embedded = false }) {
  const [events, setEvents] = useState([]);
  const [gw, setGw] = useState(null);
  const [loading, setLoading] = useState(false);
  const [top3, setTop3] = useState([]);     // can be 3 or more if random draw pending at 3rd
  const [others, setOthers] = useState([]);
  const [elementTeamCodes, setElementTeamCodes] = useState({});

  useEffect(() => {
    const load = async () => {
      const data = await fetchFPLData();
      const evs = (data?.events || []).map((e) => ({
        id: e.id,
        name: e.name,
        deadline: new Date(e.deadline_time),
        finished: !!e.finished,
      }));
      setEvents(evs);
      const elementMap = {};
      (data?.elements || []).forEach((player) => {
        elementMap[player.id] = player.team_code || player.team || null;
      });
      setElementTeamCodes(elementMap);

      const now = new Date();
      const latest =
        evs.filter((e) => e.deadline <= now || e.finished).sort((a, b) => b.id - a.id)[0]?.id ??
        evs[0]?.id ??
        1;
      setGw(latest);
    };
    load();
  }, []);

  // ---------- helpers ----------

  const shirtFromTeamCode = (teamCode) =>
    teamCode
      ? `https://fantasy.premierleague.com/dist/img/shirts/standard/shirt_${teamCode}-66.png`
      : null;
  const currentTeamCodeFor = (player) => elementTeamCodes[player?.element] || player?.team_code || null;
  const captainImg = (row) => {
    const captainPlayer = (row?.squad || []).find((p) => p.is_captain);
    return shirtFromTeamCode(elementTeamCodes[captainPlayer?.element] || row?.captain_team_code);
  };

  const chipInfo = (chip) => {
    if (!chip) return null;
    const map = {
      "3xc": { label: "TC", title: "Triple Captain", cls: "bg-amber-100 text-amber-700 ring-amber-300" },
      bboost: { label: "BB", title: "Bench Boost", cls: "bg-indigo-100 text-indigo-700 ring-indigo-300" },
      freehit: { label: "FH", title: "Free Hit", cls: "bg-rose-100 text-rose-700 ring-rose-300" },
      wildcard: { label: "WC", title: "Wildcard", cls: "bg-teal-100 text-teal-800 ring-teal-300" },
    };
    return map[chip] || { label: chip?.toUpperCase?.() ?? "CHIP", title: "Chip used", cls: "bg-gray-100 text-gray-700 ring-gray-300" };
  };

  // TieAnalyzer-based resolver:
  // Returns: {winnerId, reason, stillTie, tieStats, tieComparison}
  // Tie-break order:
  // 1) Most goals scored
  // 2) Fewest goals conceded
  // 3) Fewest yellow cards
  // 4) Fewest red cards
  // 5) Stable virtual coin toss
  async function resolveTieForTop(targetGw, tiedRows) {
    if (!tiedRows?.length) return { winnerId: null, reason: null, stillTie: false, tieStats: null, tieComparison: null };
    if (tiedRows.length === 1) {
      return { winnerId: tiedRows[0].manager_id, reason: null, stillTie: false, tieStats: null, tieComparison: null };
    }

    const anchorId = tiedRows[0].manager_id;
    const stats = {}; // manager_id -> tie-break stats

    const normalizeTieStats = (teamStats, row) => ({
      goals_scored: Number(teamStats?.goals_scored || 0),
      goals_conceded: Number(teamStats?.goals_conceded || 0),
      yellow_cards: Number(teamStats?.yellow_cards || 0),
      red_cards: Number(teamStats?.red_cards || 0),
      bench_boost: !!teamStats?.bench_boost,
      tiebreak_player_count: Number(teamStats?.tiebreak_player_count || (teamStats?.bench_boost ? 15 : 11)),
      manager_name: teamStats?.manager_name || row?.manager_name || "",
      team_name: teamStats?.team_name || row?.team_name || "",
    });

    // First call to capture anchor as team1
    const firstOpp = tiedRows[1].manager_id;
    try {
      const first = await fetchTieAnalyzer(anchorId, firstOpp, targetGw);
      if (first?.team1 && typeof first.team1.goals_scored === "number") {
        stats[anchorId] = normalizeTieStats(first.team1, tiedRows[0]);
      }
    } catch {/* ignore */}

    // Other contestants vs anchor
    await Promise.all(
      tiedRows.slice(1).map(async (r) => {
        try {
          const resp = await fetchTieAnalyzer(anchorId, r.manager_id, targetGw);
          if (resp?.team2 && typeof resp.team2.goals_scored === "number") {
            stats[r.manager_id] = normalizeTieStats(resp.team2, r);
          }
        } catch {/* ignore */}
      })
    );

    // Fallback any missing analyzer response using squad stats already returned by /gw-top.
    // Respect Bench Boost: all 15 count; otherwise only positions 1-11 count.
    tiedRows.forEach((r) => {
      if (!stats[r.manager_id]) {
        const fullSquad = Array.isArray(r?.squad) ? r.squad : [];
        const isBenchBoost = r?.active_chip === "bboost";
        const squad = isBenchBoost
          ? fullSquad
          : fullSquad.filter((p) => Number(p?.position) <= 11);
        stats[r.manager_id] = {
          goals_scored: squad.reduce((sum, p) => sum + Number(p?.stats?.goals || 0), 0),
          goals_conceded: squad.reduce((sum, p) => sum + Number(p?.stats?.gc || 0), 0),
          yellow_cards: squad.reduce((sum, p) => sum + Number(p?.stats?.yc || 0), 0),
          red_cards: squad.reduce((sum, p) => sum + Number(p?.stats?.rc || 0), 0),
          bench_boost: isBenchBoost,
          tiebreak_player_count: isBenchBoost ? 15 : 11,
          manager_name: r?.manager_name || "",
          team_name: r?.team_name || "",
        };
      }
    });

    // Sort by the full tie-break order.
    const sorted = [...tiedRows].sort((A, B) => {
      const a = stats[A.manager_id];
      const b = stats[B.manager_id];
      if (b.goals_scored !== a.goals_scored) return b.goals_scored - a.goals_scored; // more is better
      if (a.goals_conceded !== b.goals_conceded) return a.goals_conceded - b.goals_conceded; // fewer is better
      if (a.yellow_cards !== b.yellow_cards) return a.yellow_cards - b.yellow_cards; // fewer is better
      if (a.red_cards !== b.red_cards) return a.red_cards - b.red_cards; // fewer is better
      return 0;
    });

    const top = stats[sorted[0].manager_id];
    const anyoneEqualTop = sorted.filter((r) => {
      const x = stats[r.manager_id];
      return x.goals_scored === top.goals_scored
        && x.goals_conceded === top.goals_conceded
        && x.yellow_cards === top.yellow_cards
        && x.red_cards === top.red_cards;
    });

    if (anyoneEqualTop.length > 1) {
      // Final tie-break: stable virtual coin toss. Same GW + same finalists => same winner on Refresh.
      const finalists = [...anyoneEqualTop].sort((a, b) => Number(a.manager_id) - Number(b.manager_id));
      const seedText = `${targetGw}:${finalists.map((r) => r.manager_id).join(":")}`;
      let hash = 2166136261;
      for (let i = 0; i < seedText.length; i += 1) {
        hash ^= seedText.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
      }
      const winner = finalists[Math.abs(hash >>> 0) % finalists.length];
      const otherFinalists = finalists.filter((r) => r.manager_id !== winner.manager_id);
      return {
        winnerId: winner.manager_id,
        reason: "virtual_coin_toss",
        stillTie: false,
        tieStats: stats[winner.manager_id],
        tieComparison: {
          winner: { manager_id: winner.manager_id, ...stats[winner.manager_id] },
          runnerUp: otherFinalists[0]
            ? { manager_id: otherFinalists[0].manager_id, ...stats[otherFinalists[0].manager_id] }
            : null,
          finalistCount: finalists.length,
        },
      };
    }

    // Determine which condition actually separated first from second.
    const second = sorted[1];
    const a = stats[sorted[0].manager_id];
    const b = stats[second.manager_id];
    let reason = "red_cards";
    if (a.goals_scored !== b.goals_scored) reason = "goals_scored";
    else if (a.goals_conceded !== b.goals_conceded) reason = "goals_conceded";
    else if (a.yellow_cards !== b.yellow_cards) reason = "yellow_cards";
    else if (a.red_cards !== b.red_cards) reason = "red_cards";

    return {
      winnerId: sorted[0].manager_id,
      reason,
      stillTie: false,
      tieStats: stats[sorted[0].manager_id],
      tieComparison: {
        winner: { manager_id: sorted[0].manager_id, ...stats[sorted[0].manager_id] },
        runnerUp: second ? { manager_id: second.manager_id, ...stats[second.manager_id] } : null,
        finalistCount: tiedRows.length,
      },
    };
  }

  // pick strict Top 1/2/3 using tie rules
  function removeByIds(pool, ids) {
    const set = new Set(ids);
    return pool.filter((r) => !set.has(r.manager_id));
  }

async function pickOnePosition(targetGw, pool) {
  if (pool.length === 0) return { picks: [], remaining: pool };

  const bestScore = Math.max(...pool.map((r) => Number(r.net_points ?? -1)));
  const candidates = pool.filter((r) => Number(r.net_points) === bestScore);

  if (candidates.length === 1) {
    return { picks: [{ ...candidates[0] }], remaining: removeByIds(pool, [candidates[0].manager_id]) };
  }

  // Resolve with TieAnalyzer
  const { winnerId, reason, stillTie, tieStats, tieComparison } = await resolveTieForTop(targetGw, candidates);

  if (stillTie || !winnerId) {
    // For random draw pending, keep ALL candidates in the same position group
    const marked = candidates.map((r) => ({
      ...r,
      _isTie: true,
      _tieReason: "random_draw_pending",
      _randomDrawPending: true,
    }));
    return { picks: marked, remaining: removeByIds(pool, candidates.map((c) => c.manager_id)) };
  }

  // FIX: Only remove the WINNER, keep the losers in pool for next positions
  const markedCandidates = candidates.map((r) => ({
    ...r,
    _isTie: true,
    _isTieWinner: r.manager_id === winnerId,
    _tieReason: reason,
    _tieStats: r.manager_id === winnerId ? tieStats : null,
    _tieComparison: r.manager_id === winnerId ? tieComparison : null,
  }));
  
  const winnerRow = markedCandidates.find((r) => r._isTieWinner);
  // Only remove winner, losers stay in pool for next positions
  return { picks: [winnerRow], remaining: removeByIds(pool, [winnerId]) };
}

  const handleFetch = async (targetGw) => {
    setLoading(true);

    // fetch both gwTop and full standings
    const gwTop = await fetchGwTop(leagueId, targetGw);
    const standings = await fetchClassicLeague(leagueId, targetGw);

    // rank map
    const rankMap = {};
    standings.forEach((s) => { rankMap[s.id] = s.rank; });

    // merge
    const top3WithRank = (gwTop?.top3 || []).map((r) => ({ ...r, league_rank: rankMap[r.manager_id] || "-" }));
    const othersWithRank = (gwTop?.others || []).map((r) => ({ ...r, league_rank: rankMap[r.manager_id] || "-" }));

    // Build a single pool to choose Top 1,2,3 strictly with tie rules
    // Compute net_points (points - transferCost)
const allRows = [...top3WithRank, ...othersWithRank].map((r) => ({
  ...r,
  // Prefer the API's authoritative adjusted score. The fallback keeps the page
  // compatible with an older/cached backend response during deployment.
  net_points: Number.isFinite(Number(r.adjusted_score))
    ? Number(r.adjusted_score)
    : (Number(r.gw_points) || 0) - (Number(r.transfers_cost) || 0),
}));
let pool = [...allRows];

    const chosen = [];

    // Pick the configured number of GW winners.
    for (let i = 0; i < winnerCount; i++) {
      const { picks, remaining } = await pickOnePosition(targetGw, pool);
      if (picks.length === 0) break;
      chosen.push(...picks);
      pool = remaining;
      // If a position returned multiple (random draw pending), we still proceed:
      // - For 1st or 2nd, we continue to pick next position from *remaining* pool.
      // - For 3rd, it's fine to have >3 cards; we'll display them all.
    }

    // If we selected fewer than 3 cards (because pool ended early), top up from pool by score desc
    if (chosen.length < winnerCount && pool.length > 0) {
      const sortedPool = [...pool].sort((a, b) => Number(b.net_points) - Number(a.net_points));

      while (chosen.length < winnerCount && sortedPool.length) chosen.push(sortedPool.shift());
      pool = sortedPool;
    }

    // Compute Others = everything not in chosen (by manager_id)
    const chosenIds = new Set(chosen.map((c) => c.manager_id));
    let othersArr = allRows.filter((r) => !chosenIds.has(r.manager_id));

    // Mark ANY score ties (visual badge) across *all* rows we will render
    const everyone = [...chosen, ...othersArr];
    const scoreCounts = {};
    everyone.forEach((r) => {
      const s = Number(r.net_points);

      if (!Number.isNaN(s)) scoreCounts[s] = (scoreCounts[s] || 0) + 1;
    });
    const markScoreTies = (rows) =>
      rows.map((r) => ({
        ...r,
        _hasScoreTie: scoreCounts[Number(r.net_points)] > 1,
      }));

    setTop3(markScoreTies(chosen));
    setOthers(markScoreTies(othersArr));
    setLoading(false);
  };

  useEffect(() => {
    if (gw) handleFetch(gw);
    // eslint-disable-next-line
  }, [gw, leagueId, winnerCount]);

  const gwOptions = useMemo(
    () =>
      events.map((e) => (
        <option key={e.id} value={e.id}>
          {e.name}
        </option>
      )),
    [events]
  );

  // ----- styles -----
  const rankCardStyles = [
    {
      ring: "ring-amber-300/70",
      panel: "from-amber-50 via-amber-100 to-amber-50",
      medal: "1",
      title: "text-amber-900",
      score: "text-amber-700",
    },
    {
      ring: "ring-slate-300/80",
      panel: "from-slate-50 via-slate-100 to-white",
      medal: "2",
      title: "text-slate-900",
      score: "text-slate-700",
    },
    {
      ring: "ring-orange-300/70",
      panel: "from-orange-50 via-orange-100 to-white",
      medal: "3",
      title: "text-orange-900",
      score: "text-orange-700",
    },
  ];

  const normalizedPlayerName = (name = "") =>
    String(name)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

  // FPL/PL portrait assets can lag behind real-world transfers. Keep a tiny local
  // override map for players whose official portrait is still wearing an old club kit.
  const customCaptainPortrait = (row) => {
    const name = normalizedPlayerName(row?.captain_name);
    if (name === "joao pedro") return "/assets/players/joao-pedro-chelsea.png";
    return null;
  };

  const playerPortrait = (row) =>
    customCaptainPortrait(row) ||
    (row?.captain_code
      ? `https://resources.premierleague.com/premierleague/photos/players/250x250/p${row.captain_code}.png`
      : captainImg(row));

  const benchPoints = (row) =>
    (row?.squad || [])
      .filter((p) => Number(p.position || 0) > 11)
      .reduce((sum, p) => sum + Number(p.points || 0), 0);

  const netScore = (row) =>
    Number.isFinite(Number(row?.adjusted_score))
      ? Number(row.adjusted_score)
      : Number(row?.gw_points || 0) - Number(row?.transfers_cost || 0);

  const MiniSquad = ({ row }) => {
    const squad = row?.squad || [];
    if (!squad.length) return null;

    const starters = squad.filter((p) => Number(p.position || 0) <= 11);
    const bench = squad.filter((p) => Number(p.position || 0) > 11);
    const groups = [
      starters.filter((p) => Number(p.element_type) === 1),
      starters.filter((p) => Number(p.element_type) === 2),
      starters.filter((p) => Number(p.element_type) === 3),
      starters.filter((p) => Number(p.element_type) === 4),
    ];

    const fallback = !groups.some((g) => g.length);
    const rows = fallback
      ? [starters.slice(0, 1), starters.slice(1, 5), starters.slice(5, 8), starters.slice(8, 11)]
      : groups;

    const MiniPlayer = ({ player, benchPlayer = false }) => {
      const teamCode = currentTeamCodeFor(player);
      const rawPoints = Number(player.points || 0);
      const apiMultiplier = Number(player.multiplier || 0);
      const fallbackCaptainMultiplier = player.is_captain ? (row?.chip === "3xc" ? 3 : 2) : 1;
      // Prefer FPL's picks multiplier. The fallback also makes older/cached gw-top
      // payloads immediately show the captain contribution correctly.
      const scoringMultiplier = apiMultiplier > 1 ? apiMultiplier : fallbackCaptainMultiplier;
      const displayedPoints = rawPoints * scoringMultiplier;
      return (
        <div className={`min-w-0 text-center ${benchPlayer ? "gw-mini-bench-player" : "gw-mini-player"}`}>
          <div className="gw-mini-shirt-wrap">
            {teamCode ? (
              <img
                src={shirtFromTeamCode(teamCode)}
                alt=""
                className="gw-mini-shirt"
                loading="lazy"
              />
            ) : (
              <div className="gw-mini-shirt-fallback" />
            )}
            {player.is_captain && <span className="gw-mini-badge gw-mini-badge-c">C</span>}
            {player.is_vice_captain && <span className="gw-mini-badge gw-mini-badge-v">V</span>}
          </div>
          <div className="gw-mini-name">{player.web_name || player.name || player.player_name || `#${player.element}`}</div>
          <div className="gw-mini-points">
            {displayedPoints} pts
          </div>
        </div>
      );
    };

    return (
      <div className="gw-official-pitch">
        <div className="gw-pitch-brand"><span>FPL</span><span>NEPAL</span></div>
        <div className="gw-pitch-lines" aria-hidden="true">
          <span className="gw-pitch-box gw-pitch-box-top" />
          <span className="gw-pitch-circle" />
          <span className="gw-pitch-half" />
          <span className="gw-pitch-box gw-pitch-box-bottom" />
        </div>
        <div className="gw-pitch-players">
          {rows.filter((r) => r.length).map((line, i) => (
            <div key={i} className="gw-pitch-row">
              {line.map((player) => <MiniPlayer key={`${player.element}-${player.position}`} player={player} />)}
            </div>
          ))}
        </div>
        {bench.length > 0 && (
          <div className="gw-pitch-bench">
            <div className="gw-pitch-bench-title">Substitutes</div>
            <div className="gw-pitch-bench-row">
              {bench.map((player) => <MiniPlayer benchPlayer key={`b-${player.element}-${player.position}`} player={player} />)}
            </div>
          </div>
        )}
      </div>
    );
  };

  const FeaturedWinner = ({ row }) => {
    const chip = chipInfo(row?.chip);
    const portrait = playerPortrait(row);
    const chipTitle = chip?.title || "No chip used";
    return (
      <article className="gw-winner-featured overflow-hidden rounded-[30px] bg-white shadow-2xl ring-1 ring-slate-200 mb-8">
        <div className="grid lg:grid-cols-[.82fr_1.18fr]">
          <div className="relative overflow-hidden bg-gradient-to-br from-[#00c9e8] via-[#3154ff] to-[#7d00c8] text-white p-7 sm:p-9 lg:p-10">
            <div className="gw-confetti" aria-hidden="true">{Array.from({length:18}).map((_,i)=><span key={i} />)}</div>
            <div className="absolute inset-0 opacity-30" style={{backgroundImage:'linear-gradient(120deg, transparent 0 44%, rgba(255,255,255,.2) 44% 45%, transparent 45% 100%), linear-gradient(30deg, transparent 0 72%, rgba(255,255,255,.15) 72% 73%, transparent 73% 100%)', backgroundSize:'180px 180px, 220px 220px'}} />
            <div className="absolute -right-24 -top-24 w-80 h-80 rounded-full bg-fuchsia-400/30 blur-3xl" />
            <div className="relative z-10 h-full flex flex-col">
              <div>
                <div className="inline-flex items-center rounded-full bg-[#37003c] px-4 py-2 text-[11px] sm:text-xs font-black tracking-[0.2em] uppercase shadow-lg">
                  FPL Nepal · {title}
                </div>
                <div className="mt-7 text-sm font-black uppercase tracking-[0.18em] text-white/85">Gameweek {gw}</div>
                <h2 className="mt-1 text-4xl sm:text-5xl lg:text-[56px] font-black leading-[0.9] tracking-tight">GAMEWEEK<br/>WINNER</h2>
              </div>

              <div className="mt-8">
                <div className="rounded-3xl bg-[#37003c]/78 backdrop-blur-sm ring-1 ring-white/20 p-5 sm:p-6 shadow-xl">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-cyan-200 font-black">Winning Manager</div>
                  <div className="mt-1 text-3xl sm:text-4xl font-black tracking-tight">{row.manager_name}</div>
                  <div className="mt-1 text-base font-bold text-white/75">{row.team_name}</div>
                  <div className="mt-5 grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-black">Adjusted Score</div>
                      <div className="text-5xl sm:text-6xl font-black text-cyan-200 leading-none">{netScore(row)}</div>
                      {Number(row.transfers_cost || 0) > 0 && (
                        <div className="mt-1 text-xs font-bold text-rose-200">
                          {row.gw_points} pts − {row.transfers_cost} hit
                        </div>
                      )}
                    </div>
                    <div className="self-end pb-1">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-black">League Position</div>
                      <div className="text-2xl sm:text-3xl font-black">#{row.league_rank}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-6">
                <div className="rounded-2xl bg-white/15 ring-1 ring-white/20 px-4 py-3">
                  <div className="text-[10px] uppercase text-white/65 font-black">Bench</div>
                  <div className="mt-0.5 font-black text-xl">{benchPoints(row)} pts</div>
                </div>
                <div className="rounded-2xl bg-white/15 ring-1 ring-white/20 px-4 py-3">
                  <div className="text-[10px] uppercase text-white/65 font-black">Transfers</div>
                  <div className="mt-0.5 font-black text-xl">{Number(row.event_transfers || 0)}</div>
                </div>
                <div className={`col-span-2 rounded-2xl px-4 py-4 ring-1 shadow-lg ${chip ? "bg-[#00ff87] text-[#37003c] ring-white/50" : "bg-white/15 text-white ring-white/20"}`}>
                  <div className="text-[10px] uppercase font-black tracking-[0.18em] opacity-70">Chip</div>
                  <div className="mt-0.5 text-xl sm:text-2xl font-black">{chipTitle}</div>
                  {chip && <div className="text-xs font-bold opacity-75 mt-0.5">Used in Gameweek {gw}</div>}
                </div>
              </div>

              {row._isTie && row._isTieWinner && (
                <div className="mt-6 rounded-2xl bg-[#37003c]/72 backdrop-blur-sm ring-1 ring-white/20 px-4 py-4 shadow-lg">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-cyan-200 font-black">
                    <span aria-hidden="true">⚖️</span> Won by tie-breaker
                  </div>
                  <div className="mt-1.5 text-lg sm:text-xl font-black text-white">
                    {row._tieReason === "goals_scored" && "Most goals scored"}
                    {row._tieReason === "goals_conceded" && "Fewest goals conceded"}
                    {row._tieReason === "yellow_cards" && "Fewest yellow cards"}
                    {row._tieReason === "red_cards" && "Fewest red cards"}
                    {row._tieReason === "virtual_coin_toss" && "Virtual coin toss"}
                    {!['goals_scored', 'goals_conceded', 'yellow_cards', 'red_cards', 'virtual_coin_toss'].includes(row._tieReason) && "Tie-break rule"}
                  </div>
                  <div className="mt-1 text-xs font-bold text-white/70">
                    Same adjusted GW score · FPL Nepal tie-break rule
                  </div>
                  {row._tieStats && (
                    <div className="mt-3 pt-3 border-t border-white/15 grid grid-cols-2 gap-x-4 gap-y-1 text-[10px] sm:text-[11px] font-bold text-white/75">
                      <div className="col-span-2 text-white/55 uppercase tracking-[0.12em] text-[9px]">
                        {row._tieStats.bench_boost ? "Bench Boost: all 15 players counted" : "Starting XI counted"}
                      </div>
                      <div>Goals scored: <span className="text-white">{row._tieStats.goals_scored}</span></div>
                      <div>Goals conceded: <span className="text-white">{row._tieStats.goals_conceded}</span></div>
                      <div>Yellow cards: <span className="text-white">{row._tieStats.yellow_cards}</span></div>
                      <div>Red cards: <span className="text-white">{row._tieStats.red_cards}</span></div>
                    </div>
                  )}
                </div>
              )}

              <div className="gw-winner-celebration-footer mt-6">
                <div className="gw-winner-celebration-kicker">Congratulations</div>
                <div className="gw-winner-celebration-title">Gameweek {gw} Winner</div>
                <div className="gw-winner-celebration-meta">FPL Nepal · {title}</div>
              </div>
            </div>
          </div>

          <div className="relative bg-gradient-to-br from-[#effcff] via-white to-[#f6efff] p-5 sm:p-6 lg:p-7 flex flex-col">
            <div className="relative min-h-[205px] sm:min-h-[225px] rounded-[24px] bg-white/80 ring-1 ring-slate-200 overflow-hidden shadow-sm">
              <div className={`relative z-10 p-5 sm:p-6 ${customCaptainPortrait(row) ? "max-w-[42%]" : "max-w-[52%]"}`}>
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-black">Captain</div>
                <div className="mt-1 text-3xl sm:text-4xl font-black text-[#37003c]">{row.captain_name}</div>
                <div className="mt-3 inline-flex items-center rounded-full bg-[#37003c] text-white px-3 py-1.5 text-xs font-black shadow">Captain</div>
                <div className="mt-6 text-[11px] uppercase tracking-[0.16em] text-slate-500 font-black">Vice Captain</div>
                <div className="mt-1 text-lg font-extrabold text-slate-800">{row.vice_name}</div>
                {chip && (
                  <div className="mt-5 inline-flex rounded-xl bg-[#00ff87] text-[#37003c] px-4 py-2 text-sm sm:text-base font-black shadow ring-1 ring-[#37003c]/10">
                    {chip.title.toUpperCase()} ACTIVE
                  </div>
                )}
              </div>
              <div className={`absolute right-0 bottom-0 h-full overflow-hidden pointer-events-none ${customCaptainPortrait(row) ? "w-[58%]" : "w-[50%]"}`}>
                {portrait && (
                  <img
                    src={portrait}
                    alt={`${row.captain_name}`}
                    className={`absolute bottom-0 max-w-full drop-shadow-2xl ${customCaptainPortrait(row) ? "gw-custom-captain-photo" : "right-3 sm:right-5 max-h-[220px] sm:max-h-[240px] object-contain"}`}
                    onError={(e) => {
                      const fallback = captainImg(row);
                      if (fallback && e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
                    }}
                  />
                )}
              </div>
            </div>

            <div className="relative z-10 mt-4 flex-1 flex flex-col">
              <div className="mb-3 flex items-end justify-between gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-black">Winning Squad</div>
                  <div className="text-base font-black text-[#37003c]">Starting XI + substitutes</div>
                </div>
                <div className="rounded-full bg-[#37003c] text-white px-3 py-1.5 text-xs font-black">GW {gw}</div>
              </div>
              <MiniSquad row={row} />
            </div>
          </div>
        </div>
      </article>
    );
  };

  const PodiumCard = ({ row, index }) => {
    const second = index === 1;
    const chip = chipInfo(row?.chip);
    const portrait = playerPortrait(row);

    const tieReasonLabel = {
      goals_scored: "Most goals scored",
      goals_conceded: "Fewest goals conceded",
      yellow_cards: "Fewest yellow cards",
      red_cards: "Fewest red cards",
      virtual_coin_toss: "Virtual coin toss",
    }[row?._tieReason] || "Tie-break rule";

    const tieMetrics = [
      { key: "goals_scored", label: "Goals scored" },
      { key: "goals_conceded", label: "Goals conceded" },
      { key: "yellow_cards", label: "Yellow cards" },
      { key: "red_cards", label: "Red cards" },
    ];
    const decidingMetricIndex = tieMetrics.findIndex((m) => m.key === row?._tieReason);
    // Show every rule checked up to the deciding rule. Example: if goals conceded
    // decides it, goals scored is also shown first so users can see that it was equal.
    const visibleTieMetrics = decidingMetricIndex >= 0
      ? tieMetrics.slice(0, decidingMetricIndex + 1)
      : [];

    const comparison = row?._tieComparison;
    const tieWinner = comparison?.winner;
    const tieRunnerUp = comparison?.runnerUp;

    return (
      <article className={`relative overflow-hidden rounded-[26px] ring-1 shadow-xl ${second ? "bg-gradient-to-br from-slate-50 via-white to-cyan-50 ring-slate-300" : "bg-gradient-to-br from-orange-50 via-white to-amber-50 ring-orange-300"}`}>
        <div className={`h-2 ${second ? "bg-gradient-to-r from-slate-300 via-cyan-400 to-slate-300" : "bg-gradient-to-r from-orange-400 via-amber-300 to-orange-400"}`} />
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className={`text-xs font-black uppercase tracking-[0.16em] ${second ? "text-slate-500" : "text-orange-700"}`}>{second ? "2nd Place" : "3rd Place"} · GW {gw}</div>
              <div className="mt-1 text-2xl sm:text-3xl font-black text-[#37003c] truncate">{row.manager_name}</div>
              <div className="text-sm font-semibold text-slate-600 truncate">{row.team_name}</div>
            </div>
            <div className={`shrink-0 rounded-2xl px-4 py-2 text-center ${second ? "bg-slate-200 text-slate-800" : "bg-orange-200 text-orange-900"}`}>
              <div className="text-[10px] uppercase font-black opacity-70">Position</div>
              <div className="text-2xl font-black">#{row.league_rank}</div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-[94px_minmax(0,1fr)_118px] sm:grid-cols-[100px_minmax(0,1fr)_145px] gap-3 sm:gap-4 items-end">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-black">Adjusted Score</div>
              <div className={`text-5xl sm:text-6xl font-black leading-none ${second ? "text-slate-800" : "text-orange-700"}`}>{netScore(row)}</div>
              {Number(row.transfers_cost || 0) > 0 && (
                <div className="mt-1 whitespace-nowrap text-[11px] sm:text-xs font-bold text-rose-600">
                  {row.gw_points} pts − {row.transfers_cost} hit
                </div>
              )}
            </div>

            <div className="min-w-0 self-stretch flex flex-col justify-end">
              <div className="text-sm">
                <div><span className="text-slate-500">Captain</span><div className="font-black text-slate-900">{row.captain_name}</div></div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                  <span className="text-slate-500">Bench <b className="text-slate-900">{benchPoints(row)} pts</b></span>
                  <span className="text-slate-500">Transfers <b className="text-slate-900">{Number(row.event_transfers || 0)}</b></span>
                </div>
              </div>

              {chip && <div className="mt-2 inline-flex self-start rounded-lg bg-[#00ff87] text-[#37003c] px-2.5 py-1 text-xs font-black">{chip.title}</div>}

              {row._isTie && row._isTieWinner && (
                <div className={`mt-2 rounded-xl border px-2.5 py-2 ${second ? "border-cyan-200 bg-cyan-50/80" : "border-orange-200 bg-orange-50/80"}`}>
                  <div className="flex items-center gap-1.5 leading-tight">
                    <span className="text-sm" aria-hidden="true">⚖️</span>
                    <span className={`text-[9px] font-black uppercase tracking-[0.12em] ${second ? "text-cyan-700" : "text-orange-700"}`}>Tie-break</span>
                    <span className="truncate text-[11px] font-black text-[#37003c]">· {tieReasonLabel}</span>
                  </div>

                  {tieWinner && tieRunnerUp && row._tieReason !== "virtual_coin_toss" && (
                    <>
                      <div className="mt-1 truncate text-[9px] font-bold text-slate-500">
                        {tieWinner.manager_name} vs {tieRunnerUp.manager_name}
                        {Number(comparison?.finalistCount || 0) > 2 ? ` · ${comparison.finalistCount}-way tie` : ""}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        {visibleTieMetrics.map((metric) => {
                          const isDecider = metric.key === row._tieReason;
                          return (
                            <div key={metric.key} className={`whitespace-nowrap text-[10px] ${isDecider ? "font-black text-[#37003c]" : "font-bold text-slate-500"}`}>
                              <span>{metric.label}: </span>
                              <span>{tieWinner[metric.key]}</span>
                              <span className="px-0.5 text-slate-400">–</span>
                              <span>{tieRunnerUp[metric.key]}</span>
                              {isDecider && <span className="ml-1 text-[9px] uppercase text-orange-700">decided</span>}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {row._tieReason === "virtual_coin_toss" && (
                    <div className="mt-1 text-[10px] font-semibold leading-tight text-slate-600">
                      All football-stat tie-breakers were identical.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="h-[132px] sm:h-[155px] rounded-2xl bg-gradient-to-b from-cyan-100 to-violet-100 overflow-hidden relative ring-1 ring-white shadow-inner">
              {portrait && <img src={portrait} alt="" className="absolute bottom-0 left-1/2 -translate-x-1/2 max-h-full max-w-[125%] object-contain" onError={(e) => { const f=captainImg(row); if(f && e.currentTarget.src !== f) e.currentTarget.src=f; }} />}
              <span className="absolute bottom-2 left-2 rounded-lg bg-[#37003c] text-white px-2 py-1 text-[10px] font-black">C · {row.captain_name}</span>
            </div>
          </div>
        </div>
      </article>
    );
  };

  const primaryWinner = top3[0] || null;
  const secondaryWinners = winnerCount > 1 ? top3.slice(1) : [];

  return (
    <div className="-mx-4 sm:-mx-6 px-4 sm:px-6 py-7 bg-gradient-to-br from-[#eefcff] via-[#f7f7ff] to-[#f8efff] min-h-full">
      <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center rounded-full bg-[#37003c] text-white px-4 py-2 text-xs uppercase tracking-[0.2em] font-black shadow-sm">FPL Nepal · {title}</div>
            <h1 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight text-[#37003c]">Gameweek {gw} Winners</h1>
            <div className="mt-1 text-sm font-bold text-slate-500">{title === "THE 100" ? "THE 100 · One Gameweek winner" : "S16 · Top three Gameweek winners"}</div>
          </div>

        <div className="flex items-center gap-3 sm:ml-auto">
          <label className="text-sm font-bold text-slate-600">Gameweek</label>
          <select
            className="border border-slate-200 bg-white rounded-xl px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
            value={gw || ""}
            onChange={(e) => setGw(Number(e.target.value))}
          >
            {gwOptions}
          </select>
          <button
            onClick={() => handleFetch(gw)}
            className="px-4 py-2 rounded-xl border border-slate-200 bg-white font-bold shadow-sm hover:shadow-md transition"
          >
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl ring-1 ring-slate-200">
          <svg className="animate-spin h-10 w-10 text-violet-600 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
          </svg>
          <p className="text-slate-600 font-bold">Loading Gameweek {gw} winners...</p>
        </div>
      ) : !primaryWinner ? (
        <div className="p-10 rounded-3xl bg-white ring-1 ring-slate-200 text-center text-slate-600 font-semibold">No winner data is available for GW {gw} yet.</div>
      ) : (
        <>
          <FeaturedWinner row={primaryWinner} />
          {secondaryWinners.length > 0 && (
            <section className="mb-8">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-lg sm:text-xl font-black text-[#37003c]">S16 Podium · 2nd & 3rd</h3>
                <span className="text-xs font-bold text-slate-500">Gameweek {gw}</span>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {secondaryWinners.map((row, i) => <PodiumCard key={`${row.manager_id}-${i}`} row={row} index={i + 1} />)}
              </div>
            </section>
          )}
        </>
      )}

      {/* Others */}
      <div className="bg-white rounded-2xl shadow-xl ring-1 ring-black/5 overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full text-sm">
<thead className="bg-gray-50">
  <tr className="text-left">
    <th className="py-3 pl-5 pr-4 font-semibold text-gray-700">Rank</th>
    <th className="py-3 px-4 font-semibold text-gray-700">Manager</th>
    <th className="py-3 px-4 font-semibold text-gray-700">Team</th>
    <th className="py-3 px-4 font-semibold text-gray-700">Captain</th>
    <th className="py-3 px-4 font-semibold text-gray-700">GW Score</th>
    <th className="py-3 px-4 font-semibold text-gray-700 text-right">Hit</th>
    <th className="py-3 px-4 font-semibold text-gray-700">Adjusted Score</th>
    <th className="py-3 px-4 font-semibold text-gray-700">Bench</th>
  </tr>
</thead>


            <tbody className="divide-y divide-gray-100">
              {others.map((row, idx) => {
                const tieTitle =
                  row._tieReason === "goals_conceded"
                    ? "Tie resolved: fewest goals conceded"
                    : row._tieReason === "random_draw_pending"
                    ? "Random draw will be held"
                    : "Tie resolved: most goals scored";
                return (
                  <tr key={row.manager_id} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="py-3 pl-5 pr-4 font-semibold">{row.league_rank}</td>
                    <td className="py-3 px-4">
                      <a
                        href={`https://fantasy.premierleague.com/entry/${row.manager_id}/event/${gw}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-semibold text-black hover:underline"
                      >
                        {row.manager_name}
                      </a>

                    </td>
                    <td className="py-3 px-4 text-gray-700">{row.team_name}</td>
                    <td className="py-3 px-4 font-medium">{row.captain_name}</td>
{/* GW Score */}
<td className="py-3 px-4 font-semibold">{row.gw_points}</td>

{/* Hit column */}
<td className="py-3 px-4 text-right font-semibold text-red-600">
  {row.transfers_cost > 0 ? `-${row.transfers_cost}` : "0"}
</td>

{/* Adjusted score */}
<td className="py-3 px-4 font-bold">{netScore(row)}</td>

{/* ✅ New Bench column */}
<td className="py-3 px-4 text-gray-700">
  {row.squad
    ? row.squad
        .filter(p => (p.position ?? 0) > 11)
        .reduce((sum, p) => sum + Number(p.points ?? 0), 0)
    : 0}
</td>


                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <ul className="md:hidden divide-y divide-gray-100">
          {others.map((row) => {
            const tieTitle =
              row._tieReason === "goals_conceded"
                ? "Tie resolved: fewest goals conceded"
                : row._tieReason === "random_draw_pending"
                ? "Random draw will be held"
                : "Tie resolved: most goals scored";
            return (
              <li key={`m-${row.manager_id}`} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full grid place-items-center bg-gray-100 text-gray-800 font-extrabold">
                      {row.rank}
                    </div>
                    <div>
                      <div className="font-semibold leading-tight flex items-center flex-wrap">
                        {row.manager_name}

                      </div>

                      <div className="text-xs text-gray-600">{row.team_name}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Adjusted Score</div>
                    <div className="text-base font-semibold">{netScore(row)}</div>
                    <div className="text-[11px] text-gray-500">
                      {row.gw_points} pts{Number(row.transfers_cost || 0) > 0 ? ` − ${row.transfers_cost} hit` : ""}
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-sm">
                  <span className="text-gray-500">Captain: </span>
                  <span className="font-medium">{row.captain_name}</span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <p className="mt-4 text-xs text-gray-500">Adjusted Score = GW Score minus transfer hit.</p>
      </div>
    </div>
  );
}
